// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import Layout from "./central/Layout";
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import CreateLeague from "./pages/CreateLeague";
import JoinLeague from "./pages/JoinLeague";
import ViewTeam from "./pages/ViewTeam";
import PickTeamV2 from "./pages/PickTeamV2";
import { LeagueProvider } from "./context/LeagueContext";
import { SeasonProvider } from "./context/SeasonContext";
import YourPoints from "./pages/YourPoints";
import Leaderboard from "./pages/Leaderboard";
import Statistics from "./pages/Statistics";
import MatchInput from "./pages/MatchInput";
import MatchResults from "./pages/MatchResults";
import ManageLeague from "./pages/ManageLeague";
import League from "./pages/League";
import PlayerPrices from "./pages/PlayerPrices";
import { ThemeProvider } from "styled-components";
import theme  from "./central/theme";
import GlobalStyle from "./central/GlobalStyle";
import Profile from "./pages/Profile";
import FAQ from "./pages/FAQ";
import AppStatus from "./pages/AppStatus";
import HelpContact from "./pages/HelpContact";
import { ToastProvider } from "./context/ToastContext";
import ForegroundNotificationHandler from "./components/ForegroundNotificationHandler";
import EndOfSeason from "./pages/EndOfSeason";
import EndOfSeasonReview from "./pages/EndOfSeasonReview";
import DreamTeam from "./pages/DreamTeam";
import Availability from "./pages/Availability";
import { useAuth } from "./context/AuthContext";
import { auth } from "./lib/firebase";

// Persistent layout wrapper — rendered once, never unmounted on league-page navigation
function LeagueLayout() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function LeagueHomeRoute() {
  const { currentUser, loading } = useAuth();

  // After sign-in, Firebase may have `auth.currentUser` before our AuthContext
  // has finished fetching/merging the Firestore user doc.
  const firebaseUser = auth.currentUser;

  // While AuthContext is still initializing, don’t redirect.
  if (loading) return null;

  // If Firebase says we’re signed in, allow /home even if context hasn’t hydrated yet.
  if (!currentUser && firebaseUser) return <Home />;

  // If neither Firebase nor our context has a user, bounce to landing.
  if (!currentUser && !firebaseUser) return <Navigate to="/" replace />;

  return <Home />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <ToastProvider>
        <ForegroundNotificationHandler />
        <Router>
          <LeagueProvider>
            <SeasonProvider>
            <Routes>
              {/* Public landing + auth */}
              <Route path="/" element={<Landing />} />
              <Route path="/home" element={<LeagueHomeRoute />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/FAQ" element={<FAQ />} />
              <Route path="/status" element={<AppStatus />} />
              <Route path="/help" element={<HelpContact />} />

              {/* League setup */}
              <Route path="/create" element={<CreateLeague />} />
              <Route path="/join" element={<JoinLeague />} />

              {/* League pages — share a single persistent Layout instance (no flicker) */}
              <Route element={<LeagueLayout />}>
                <Route path="/team/:leagueID" element={<ViewTeam />} />
                <Route path="/pickteam/:leagueID" element={<PickTeamV2 />} />
                <Route path="/yourpoints/:leagueID" element={<YourPoints />} />
                <Route path="/yourpoints/:leagueID/:teamID" element={<YourPoints />} />
                <Route path="/leaderboard/:leagueID" element={<Leaderboard />} />
                <Route path="/statistics/:leagueID" element={<Statistics />} />
                <Route path="/admin/:leagueID" element={<ManageLeague />} />
                <Route path="/league/:leagueID" element={<League />} />
                <Route path="/playerprices/:leagueID" element={<PlayerPrices />} />
                <Route path="/matchinput/:leagueID/:gameweek" element={<MatchInput />} />
                <Route path="/matchinput/:leagueID/:gameweek/:matchID" element={<MatchInput />} />
                <Route path="/matches/:leagueID/:gameweek" element={<MatchResults />} />
                <Route path="/dreamteam/:leagueID" element={<DreamTeam />} />
                <Route path="/endofseason/:leagueID" element={<EndOfSeason />} />
                <Route path="/endofseason/:leagueID/review" element={<EndOfSeasonReview />} />
                <Route path="/availability/:leagueID" element={<Availability />} />
              </Route>
              

            </Routes>
            </SeasonProvider>
          </LeagueProvider>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;